import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import produce from 'immer';
import { isEqual } from 'lodash-es';
import { makeBillUpfrontState } from '../service-billing/makers';
import { EffectiveDateSource } from './model';
import { isAwaitingApproval, isBilledUpfront, isPaused, isProrated, isSkipBillingOnAcceptance, startBillingAt as startBillingAtSelector } from './selectors';
export var ToggledField = /*#__PURE__*/ function(ToggledField) {
    ToggledField["IsBilledUpfront"] = "isBilledUpfront";
    ToggledField["IsPaused"] = "isPaused";
    ToggledField["IsProrated"] = "isProrated";
    ToggledField["BillOnAcceptance"] = "billOnAcceptance";
    ToggledField["StartBillingAt"] = "startBillingAt";
    return ToggledField;
}({});
/** Toggles billing properties for a new (awaiting-approval) ongoing service. */ export function toggleNewOngoingBilling(service, field, additionalOptions) {
    const currentStartBillingAt = startBillingAtSelector(service);
    const relevantBilling = {
        isBilledUpfront: isBilledUpfront(service),
        isPaused: isPaused(service),
        isProrated: isProrated(service),
        billUpfrontState: makeBillUpfrontState(isSkipBillingOnAcceptance(service)),
        startBillingAt: (additionalOptions === null || additionalOptions === void 0 ? void 0 : additionalOptions.effectiveDate) ? false : !!currentStartBillingAt
    };
    const mode = toNewOngoingBillingMode(relevantBilling);
    const newMode = changeNewOngoingBillingMode(mode, field);
    const newState = fromNewOngoingBillingMode(newMode);
    return produce(service, (s)=>{
        const { startBillingAt } = newState, billing = _object_without_properties(newState, [
            "startBillingAt"
        ]);
        s.billing = _object_spread({}, s.billing, billing);
        if (isAwaitingApproval(s.status)) {
            var _ref;
            s.status.awaitingApproval.effectiveDate = startBillingAt ? (_ref = additionalOptions === null || additionalOptions === void 0 ? void 0 : additionalOptions.effectiveDate) !== null && _ref !== void 0 ? _ref : currentStartBillingAt : null;
            s.status.awaitingApproval.effectiveDateSource = startBillingAt ? EffectiveDateSource.User : null;
        }
    });
}
export const getValidOngoingBilling = (relevantBilling)=>{
    const _getValidNewOngoingBilling = getValidNewOngoingBilling(relevantBilling), { startBillingAt: _ } = _getValidNewOngoingBilling, billing = _object_without_properties(_getValidNewOngoingBilling, [
        "startBillingAt"
    ]);
    return billing;
};
/** Returns valid billing properties for a new (awaiting-approval) ongoing service. */ export function getValidNewOngoingBilling(billing) {
    return fromNewOngoingBillingMode(toNewOngoingBillingMode(billing));
}
export function isValidNewOngoingBilling(billing) {
    return isEqual(getValidNewOngoingBilling(billing), billing);
}
var NewOngoingBillingMode = /*#__PURE__*/ function(NewOngoingBillingMode) {
    NewOngoingBillingMode["UpfrontAndSkipBillingOnAcceptance"] = "UpfrontAndSkipBillingOnAcceptance";
    NewOngoingBillingMode["UpfrontAndBillOnAcceptance"] = "UpfrontAndBillOnAcceptance";
    NewOngoingBillingMode["UpfrontAndBillOnAcceptanceAndProrate"] = "UpfrontAndBillOnAcceptanceAndProrate";
    NewOngoingBillingMode["UpfrontAndStartPaused"] = "UpfrontAndStartPaused";
    NewOngoingBillingMode["NotUpfront"] = "NotUpfront";
    NewOngoingBillingMode["NotUpfrontAndStartPaused"] = "NotUpfrontAndStartPaused";
    NewOngoingBillingMode["NotUpfrontAndProrate"] = "NotUpfrontAndProrate";
    NewOngoingBillingMode["UpfrontAndProrateAndStartBillingAt"] = "UpfrontAndProrateAndStartBillingAt";
    NewOngoingBillingMode["NotUpfrontAndProrateAndStartBillingAt"] = "NotUpfrontAndProrateAndStartBillingAt";
    NewOngoingBillingMode["UpfrontAndStartBillingAt"] = "UpfrontAndStartBillingAt";
    NewOngoingBillingMode["NotUpfrontAndStartBillingAt"] = "NotUpfrontAndStartBillingAt";
    return NewOngoingBillingMode;
}(NewOngoingBillingMode || {});
function toNewOngoingBillingMode(billing) {
    if (billing.startBillingAt) {
        if (billing.isBilledUpfront) {
            return billing.isProrated ? "UpfrontAndProrateAndStartBillingAt" : "UpfrontAndStartBillingAt";
        } else {
            return billing.isProrated ? "NotUpfrontAndProrateAndStartBillingAt" : "NotUpfrontAndStartBillingAt";
        }
    } else if (billing.isBilledUpfront) {
        if (billing.isPaused) {
            return "UpfrontAndStartPaused";
        }
        if (billing.billUpfrontState.skipBillingOnAcceptance) {
            return "UpfrontAndSkipBillingOnAcceptance";
        }
        if (billing.isProrated) {
            return "UpfrontAndBillOnAcceptanceAndProrate";
        }
        return "UpfrontAndBillOnAcceptance";
    } else {
        return billing.isPaused ? "NotUpfrontAndStartPaused" : billing.isProrated ? "NotUpfrontAndProrate" : "NotUpfront";
    }
}
function fromNewOngoingBillingMode(mode) {
    switch(mode){
        case "UpfrontAndSkipBillingOnAcceptance":
            return {
                isBilledUpfront: true,
                isPaused: false,
                isProrated: false,
                billUpfrontState: makeBillUpfrontState(true),
                startBillingAt: false
            };
        case "UpfrontAndBillOnAcceptance":
            return {
                isBilledUpfront: true,
                isPaused: false,
                isProrated: false,
                billUpfrontState: makeBillUpfrontState(false),
                startBillingAt: false
            };
        case "UpfrontAndBillOnAcceptanceAndProrate":
            return {
                isBilledUpfront: true,
                isPaused: false,
                isProrated: true,
                billUpfrontState: makeBillUpfrontState(false),
                startBillingAt: false
            };
        case "UpfrontAndStartPaused":
            return {
                isBilledUpfront: true,
                isPaused: true,
                isProrated: false,
                billUpfrontState: makeBillUpfrontState(true),
                startBillingAt: false
            };
        case "NotUpfront":
            return {
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                billUpfrontState: makeBillUpfrontState(true),
                startBillingAt: false
            };
        case "NotUpfrontAndStartPaused":
            return {
                isBilledUpfront: false,
                isPaused: true,
                isProrated: false,
                billUpfrontState: makeBillUpfrontState(true),
                startBillingAt: false
            };
        case "NotUpfrontAndProrate":
            return {
                isBilledUpfront: false,
                isPaused: false,
                isProrated: true,
                billUpfrontState: makeBillUpfrontState(false),
                startBillingAt: false
            };
        case "UpfrontAndProrateAndStartBillingAt":
            return {
                isBilledUpfront: true,
                isPaused: false,
                isProrated: true,
                billUpfrontState: makeBillUpfrontState(false),
                startBillingAt: true
            };
        case "NotUpfrontAndProrateAndStartBillingAt":
            return {
                isBilledUpfront: false,
                isPaused: false,
                isProrated: true,
                billUpfrontState: makeBillUpfrontState(false),
                startBillingAt: true
            };
        case "UpfrontAndStartBillingAt":
            return {
                isBilledUpfront: true,
                isPaused: false,
                isProrated: false,
                billUpfrontState: makeBillUpfrontState(false),
                startBillingAt: true
            };
        case "NotUpfrontAndStartBillingAt":
            return {
                isBilledUpfront: false,
                isPaused: false,
                isProrated: false,
                billUpfrontState: makeBillUpfrontState(false),
                startBillingAt: true
            };
    }
}
const fromUpfrontAndProrateAndStartBillingAt = (toggledField)=>{
    switch(toggledField){
        case "isBilledUpfront":
            return "NotUpfrontAndProrateAndStartBillingAt";
        case "isProrated":
            return "UpfrontAndStartBillingAt";
        case "billOnAcceptance":
            return "UpfrontAndBillOnAcceptanceAndProrate";
        case "isPaused":
            return "UpfrontAndStartPaused";
        case "startBillingAt":
            return "UpfrontAndSkipBillingOnAcceptance";
    }
};
const fromNotUpfrontAndProrateAndStartBillingAt = (toggledField)=>{
    switch(toggledField){
        case "isBilledUpfront":
            return "UpfrontAndProrateAndStartBillingAt";
        case "isProrated":
            return "NotUpfrontAndStartBillingAt";
        case "isPaused":
            return "NotUpfrontAndStartPaused";
        case "startBillingAt":
            return "NotUpfrontAndProrate";
        case "billOnAcceptance":
            return "NotUpfrontAndProrateAndStartBillingAt";
    }
};
const fromUpfrontAndStartBillingAt = (toggledField)=>{
    switch(toggledField){
        case "isBilledUpfront":
            return "NotUpfrontAndStartBillingAt";
        case "isPaused":
            return "UpfrontAndStartPaused";
        case "billOnAcceptance":
            return "UpfrontAndBillOnAcceptance";
        case "startBillingAt":
            return "UpfrontAndSkipBillingOnAcceptance";
        case "isProrated":
            return "UpfrontAndProrateAndStartBillingAt";
    }
};
const fromNotUpfrontAndStartBillingAt = (toggledField)=>{
    switch(toggledField){
        case "isBilledUpfront":
            return "UpfrontAndStartBillingAt";
        case "isProrated":
            return "NotUpfrontAndProrateAndStartBillingAt";
        case "isPaused":
            return "NotUpfrontAndStartPaused";
        case "startBillingAt":
            return "NotUpfront";
        case "billOnAcceptance":
            return "NotUpfrontAndStartBillingAt";
    }
};
function changeNewOngoingBillingMode(mode, toggledField) {
    switch(mode){
        case "UpfrontAndSkipBillingOnAcceptance":
            return fromUpfrontAndSkipBillingOnAcceptance(toggledField);
        case "UpfrontAndBillOnAcceptance":
            return fromUpfrontAndBillOnAcceptance(toggledField);
        case "UpfrontAndBillOnAcceptanceAndProrate":
            return fromUpfrontAndBillOnAcceptanceAndProrate(toggledField);
        case "UpfrontAndStartPaused":
            return fromUpfrontAndStartPaused(toggledField);
        case "NotUpfront":
            return fromNotUpfront(toggledField);
        case "NotUpfrontAndStartPaused":
            return fromNotUpfrontAndStartPaused(toggledField);
        case "NotUpfrontAndProrate":
            return fromNotUpfrontAndProrate(toggledField);
        case "UpfrontAndProrateAndStartBillingAt":
            return fromUpfrontAndProrateAndStartBillingAt(toggledField);
        case "NotUpfrontAndProrateAndStartBillingAt":
            return fromNotUpfrontAndProrateAndStartBillingAt(toggledField);
        case "UpfrontAndStartBillingAt":
            return fromUpfrontAndStartBillingAt(toggledField);
        case "NotUpfrontAndStartBillingAt":
            return fromNotUpfrontAndStartBillingAt(toggledField);
    }
}
function fromUpfrontAndSkipBillingOnAcceptance(toggledField) {
    switch(toggledField){
        case "isBilledUpfront":
            return "NotUpfront";
        case "isPaused":
            return "UpfrontAndStartPaused";
        case "isProrated":
            return "UpfrontAndSkipBillingOnAcceptance"; // Not allowed.
        case "billOnAcceptance":
            return "UpfrontAndBillOnAcceptance";
        case "startBillingAt":
            return "UpfrontAndStartBillingAt";
    }
}
function fromUpfrontAndBillOnAcceptance(toggledField) {
    switch(toggledField){
        case "isBilledUpfront":
            return "NotUpfront";
        case "isPaused":
            return "UpfrontAndStartPaused";
        case "isProrated":
            return "UpfrontAndBillOnAcceptanceAndProrate";
        case "billOnAcceptance":
            return "UpfrontAndSkipBillingOnAcceptance";
        case "startBillingAt":
            return "UpfrontAndStartBillingAt";
    }
}
function fromUpfrontAndBillOnAcceptanceAndProrate(toggledField) {
    switch(toggledField){
        case "isBilledUpfront":
            return "NotUpfrontAndProrate";
        case "isPaused":
            return "UpfrontAndStartPaused";
        case "isProrated":
            return "UpfrontAndBillOnAcceptance";
        case "billOnAcceptance":
            return "UpfrontAndSkipBillingOnAcceptance";
        case "startBillingAt":
            return "UpfrontAndProrateAndStartBillingAt";
    }
}
function fromUpfrontAndStartPaused(toggledField) {
    switch(toggledField){
        case "isBilledUpfront":
            return "NotUpfrontAndStartPaused";
        case "isPaused":
            return "UpfrontAndSkipBillingOnAcceptance";
        case "billOnAcceptance":
            return "UpfrontAndBillOnAcceptance"; // Not allowed.
        case "isProrated":
        case "startBillingAt":
            return "UpfrontAndStartBillingAt";
    }
}
function fromNotUpfront(toggledField) {
    switch(toggledField){
        case "isBilledUpfront":
            return "UpfrontAndSkipBillingOnAcceptance";
        case "isPaused":
            return "NotUpfrontAndStartPaused";
        case "isProrated":
            return "NotUpfrontAndProrate";
        case "billOnAcceptance":
            return "NotUpfront"; // Not allowed.
        case "startBillingAt":
            return "NotUpfrontAndStartBillingAt";
    }
}
function fromNotUpfrontAndStartPaused(toggledField) {
    switch(toggledField){
        case "isBilledUpfront":
            return "UpfrontAndStartPaused";
        case "isPaused":
            return "NotUpfront";
        case "isProrated":
        case "billOnAcceptance":
            return "NotUpfrontAndStartPaused"; // Not allowed.
        case "startBillingAt":
            return "NotUpfrontAndStartBillingAt";
    }
}
function fromNotUpfrontAndProrate(toggledField) {
    switch(toggledField){
        case "isBilledUpfront":
            return "UpfrontAndSkipBillingOnAcceptance";
        case "isProrated":
            return "NotUpfront";
        case "isPaused":
            return "NotUpfrontAndStartPaused";
        case "billOnAcceptance":
            return "NotUpfrontAndProrate"; // Not allowed.
        case "startBillingAt":
            return "NotUpfrontAndProrateAndStartBillingAt";
    }
}
